import * as React from "react"

/**
 * A custom React hook to evaluate a CSS media query.
 *
 * This hook listens for changes in the specified media query and returns a boolean
 * indicating whether the query matches the current viewport.
 *
 * @param query - The CSS media query to evaluate.
 * @returns A boolean indicating whether the media query matches.
 *
 * @example
 * ```tsx
 * import { useMediaQuery } from "@onnia/hooks"
 *
 * const MyComponent = () => {
 *   const isLargeScreen = useMediaQuery("(min-width: 1024px)")
 *
 *   return (
 *     <div>
 *       {isLargeScreen ? <p>Large screen</p> : <p>Small screen</p>}
 *     </div>
 *   )
 * }
 * ```
 */
export function useMediaQuery(query: string): boolean {
    const getInitial = React.useCallback(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
        try {
            return window.matchMedia(query).matches
        } catch {
            return false
        }
    }, [query])

    const [matches, setMatches] = React.useState<boolean>(getInitial)

    React.useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return
        const mql = window.matchMedia(query)
        const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
            // Some environments dispatch MediaQueryList instead of event
            setMatches("matches" in e ? e.matches : mql.matches)
        }

        // Set the initial value in effect to ensure hydration consistency
        setMatches(mql.matches)

        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", onChange as EventListener)
            return () => mql.removeEventListener("change", onChange as EventListener)
        }

        // Legacy Safari/Firefox APIs
        // Deprecated APIs for older browsers
        if ("addListener" in mql && typeof mql.addListener === "function") {
            type LegacyHandler = (this: MediaQueryList, ev: MediaQueryListEvent) => void
            const add: (listener: LegacyHandler) => void = mql.addListener.bind(mql)
            const remove: (listener: LegacyHandler) => void = (mql.removeListener || (() => {})).bind(mql)
            const legacyHandler: LegacyHandler = () => onChange(mql)
            add(legacyHandler)
            return () => remove(legacyHandler)
        }
    }, [query])

    return matches
}
